import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import {
  PageHeader,
  SectionHeader,
  Kpi,
  Card,
  RangeSelect,
  IncludeTestToggle,
  TestDataBanner,
  useIncludeTest,
  AwaitingBanner,
  pct,
} from "./adminUi";

const RANGE_OPTIONS = [
  ["1d", "Today"],
  ["7d", "7 days"],
  ["30d", "30 days"],
  ["90d", "90 days"],
  ["", "All time"],
];

/**
 * Engagement — cross-surface activity across LifeGuide, guests, gifts, and
 * the Proverb→LifeGuide funnel. Reuses `masterOverview` and `lifeguideAnalytics`
 * so no new backend query paths were introduced.
 */
export default function AdminEngagementPage() {
  const [includeTest, setIncludeTest] = useIncludeTest();
  const [range, setRange] = useState("30d");
  const [ov, setOv] = useState(null);
  const [lg, setLg] = useState(null);
  const [prvRange, setPrvRange] = useState(null);
  const [pwa, setPwa] = useState(null);
  const [diag, setDiag] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setOv(null);
    setLg(null);
    setPrvRange(null);
    setPwa(null);
    setDiag(null);
    Promise.all([
      adminApi.masterOverview({ include_test: includeTest }),
      adminApi.lifeguideAnalytics({ range, include_test: includeTest }),
      // Range-aware Proverb metrics. masterOverview.proverb is all-time
      // and ignores the range selector — using it here caused "Today" and
      // "30d" to display the same number (274 total-time proverb visits).
      adminApi
        .proverbAnalytics({ range, include_test: includeTest })
        .catch(() => null),
      adminApi
        .pwaAnalytics({ range, include_test: includeTest })
        .catch(() => null),
      adminApi
        .analyticsDiagnostics({
          range,
          include_test: includeTest,
          collection: "guest_events",
          event_type: "guest_visit",
        })
        .catch(() => null),
    ])
      .then(([o, l, p, pw, d]) => {
        setOv(o);
        setLg(l);
        setPrvRange(p);
        setPwa(pw);
        setDiag(d);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load engagement"));
  }, [includeTest, range]);

  const lgOv = ov?.lifeguide || {};
  // Prefer range-aware Proverb numbers; fall back to masterOverview only if
  // the range-aware endpoint failed. Semantics kept: "visits" = event count,
  // "searches" = event count. Distinct visitors are exposed elsewhere.
  const prv = prvRange
    ? {
        visitors: prvRange?.traffic?.total_visitors ?? null,
        unique_visitors: prvRange?.traffic?.unique_visitors ?? null,
        searches: prvRange?.search?.total_searches ?? null,
        lifeguide_clicks: prvRange?.cross_product?.lifeguide_clicks ?? null,
      }
    : ov?.proverb || {};
  const eng = lg?.engagement || {};
  const gi = lg?.gifts || {};
  const gu = lg?.guest || {};
  // Range-aware "Registered users active" — was pinned to active_30d, which
  // made Today show the same value as 30d.
  const activeInRange = lg?.growth?.active_users_in_range ?? lg?.retention?.active_30d;

  return (
    <div data-testid="admin-engagement-page" className="space-y-8">
      <PageHeader
        eyebrow="Traffic & Engagement"
        title="Engagement"
        subtitle="Cross-product traffic and engagement across LifeGuide and I Need a Proverb. Ranges are computed in America/Chicago; unavailable metrics are shown as “—”, not zero."
        right={
          <div className="flex items-center gap-4">
            <RangeSelect value={range} onChange={setRange} options={RANGE_OPTIONS} />
            <IncludeTestToggle value={includeTest} onChange={setIncludeTest} />
          </div>
        }
      />
      <TestDataBanner show={includeTest} />

      {err && (
        <div className="rounded-2xl border border-[#B4795C]/50 bg-[#241914] text-[#F3D6A6] p-4 text-sm">
          {err}
        </div>
      )}

      <section>
        <SectionHeader label="Visits & sessions" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="eng-lg-visitors" label="LifeGuide guest visitors" value={gu.visitors} />
          <Kpi testid="eng-lg-registered" label="Registered users active" value={activeInRange} />
          <Kpi testid="eng-proverb-visits" label="I Need a Proverb visits" value={prv.visitors} />
          <Kpi testid="eng-proverb-searches" label="Proverb searches" value={prv.searches} />
        </div>
      </section>

      <section>
        <SectionHeader label="LifeGuide activity" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="eng-guidance" label="Guidance sessions" value={eng.guidance_used} />
          <Kpi testid="eng-coach" label="Coach messages" value={eng.coach_used} />
          <Kpi testid="eng-journal" label="Journal entries" value={eng.journal_entries} />
          <Kpi testid="eng-saved" label="Saved wisdom" value={eng.saved_wisdom} />
          <Kpi testid="eng-analyzer" label="Message analyzer runs" value={eng.analyzer_runs} />
          <Kpi testid="eng-audio" label="Audio plays" value={eng.audio_plays} />
        </div>
      </section>

      <section>
        <SectionHeader label="Gifts & upgrades" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="eng-gift-visits" label="Gift page visits" value={gi.landing_visits} />
          <Kpi testid="eng-gifts-purchased" label="Gifts purchased" value={gi.purchased} />
          <Kpi testid="eng-gifts-redeemed" label="Gifts redeemed" value={gi.redeemed} />
          <Kpi testid="eng-upgrade-visits" label="Upgrade page visits" value={eng.upgrade_visits} />
          <Kpi testid="eng-trial-starts" label="Trial starts" value={lgOv.trial_users} />
          <Kpi
            testid="eng-trial-conv"
            label="Trial → Paid"
            value={lgOv.trial_to_paid_pct != null ? `${lgOv.trial_to_paid_pct}%` : "—"}
          />
          <Kpi testid="eng-pro-subs" label="Pro subscribers" value={lgOv.paid_pro} tone="accent" />
        </div>
      </section>

      <section>
        <SectionHeader label='LifeGuide "Save to My Phone" (PWA) — distinct from I Need a Proverb activity' />
        {pwa ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
              <Kpi testid="eng-pwa-shown"      label="Prompts shown"           value={pwa?.events?.save_prompt_shown} />
              <Kpi testid="eng-pwa-clicked"    label="Save to Phone clicked"   value={pwa?.events?.save_prompt_clicked} />
              <Kpi testid="eng-pwa-ios"        label="iPhone instructions shown" value={pwa?.events?.ios_instructions_shown} />
              <Kpi testid="eng-pwa-accepted"   label="Android install accepted" value={pwa?.events?.android_install_accepted} tone="accent" />
              <Kpi testid="eng-pwa-standalone" label="Launched standalone"     value={pwa?.events?.standalone_launch} />
            </div>
            <div className="text-[11px] text-[#607467] mt-2 flex flex-wrap gap-x-3">
              <span>Prompt → clicked: <span className="text-white">{pwa?.conversion?.shown_to_clicked_pct == null ? "—" : `${pwa.conversion.shown_to_clicked_pct}%`}</span></span>
              <span>Prompt → installed (Android): <span className="text-white">{pwa?.conversion?.shown_to_installed_pct == null ? "—" : `${pwa.conversion.shown_to_installed_pct}%`}</span></span>
              <span>By platform — iOS {pwa?.by_platform?.ios ?? "—"} · Android {pwa?.by_platform?.android ?? "—"} · Web {pwa?.by_platform?.web ?? "—"}</span>
              <span>Scope: <span className="text-white font-mono">app=lifeguide</span></span>
            </div>
          </>
        ) : (
          <AwaitingBanner label="PWA analytics will appear once Save-to-Phone events arrive from the LifeGuide app." />
        )}
      </section>

      <section>
        <SectionHeader label="Simple trend" />
        <Card>
          <TrendStrip
            rows={[
              ["Proverb searches", prv.searches ?? null],
              ["LG clicks from Proverb", prv.lifeguide_clicks ?? null],
              ["Guest visitors — 30d", lgOv.guest_visitors_30d ?? null],
              ["Guest → account — 30d", lgOv.guest_conversions_30d ?? null],
              ["Trial starts", lgOv.trial_users ?? null],
              ["Paid Pro", lgOv.paid_pro ?? null],
            ]}
          />
        </Card>
        {(prv.searches ?? 0) === 0 && (lgOv.guest_visitors_30d ?? 0) === 0 && (
          <div className="mt-3">
            <AwaitingBanner label="Trend chart will appear once daily events start flowing" />
          </div>
        )}
      </section>

      {diag && (
        <section>
          <SectionHeader label="Diagnostic — what this KPI counts" />
          <Card>
            <div
              data-testid="eng-analytics-diagnostics"
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm"
            >
              <DiagRow k="Selected range" v={diag.range} />
              <DiagRow k="Timezone" v={diag.timezone} />
              <DiagRow k="Collection" v={diag.collection} />
              <DiagRow k="Event type" v={diag.event_type} />
              <DiagRow k="Since (UTC)" v={diag.since_utc || "— (all time)"} />
              <DiagRow k="Until (UTC)" v={diag.until_utc} />
              <DiagRow k="Raw matching events" v={diag.raw_matching_events} />
              <DiagRow
                k={`Distinct ${diag.distinct_field || "visitors"}`}
                v={diag.distinct_visitors}
              />
              <DiagRow k="Excluded as test/demo" v={diag.excluded_as_test_or_demo} />
              <DiagRow k="Production status" v={diag.production_status} />
              <DiagRow
                k="Today at this moment"
                v={`${diag.same_moment_today_check.today_count} events · ${
                  diag.same_moment_today_check.today_distinct_visitors ?? "—"
                } visitors`}
              />
              <DiagRow
                k="Invariant: Today ≤ selected range"
                v={diag.same_moment_today_check.invariant_today_le_range ? "PASS" : "FAIL"}
              />
            </div>
          </Card>
        </section>
      )}
    </div>
  );
}

function DiagRow({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-white/5 py-1.5 last:border-0">
      <span className="text-[#8FA894]">{k}</span>
      <span className="text-white tabular-nums truncate max-w-[60%]" title={String(v)}>
        {v === null || v === undefined ? "—" : String(v)}
      </span>
    </div>
  );
}

function TrendStrip({ rows }) {
  const maxV = Math.max(1, ...rows.map(([, v]) => v ?? 0));
  return (
    <div className="space-y-2" data-testid="eng-trend-strip">
      {rows.map(([label, value]) => {
        const v = value ?? 0;
        const width = value == null ? 0 : Math.max(2, Math.round((v / maxV) * 100));
        return (
          <div key={label} className="grid grid-cols-[minmax(0,180px)_1fr_auto] items-center gap-3">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#8FA894] truncate">
              {label}
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full bg-[#4F6F52]"
                style={{ width: `${width}%` }}
              />
            </div>
            <div className="text-sm font-display text-white tabular-nums">
              {value == null ? "—" : v}
            </div>
          </div>
        );
      })}
    </div>
  );
}
